import { UNIT_FILE_KINDS } from './photos';

/**
 * The upload page's pure parts (MERCH-78): read the token from the URL fragment, name what the
 * upload is for, and say which types and size it takes. The server decides every rule (the
 * types, the size, whether alt text is required); this only words them.
 */
const TOKEN = /^mpu_[A-Za-z0-9_-]{43}$/;

/** The link token from `#mpu_…`, or '' when there is none or it is malformed. */
export function tokenFromHash(hash) {
  const raw = String(hash || '').replace(/^#/, '').trim();
  return TOKEN.test(raw) ? raw : '';
}

// Storefront kinds as a person reads them (lib/media/upload STOREFRONT_KINDS).
const STOREFRONT_NOUNS = {
  image: 'Storefront image', banner: 'Storefront banner', logo: 'Store logo', favicon: 'Store favicon', poster: '3D poster image',
  model_desktop: '3D model (desktop)', model_mobile: '3D model (mobile)',
};

/**
 * The heading for a link, by owner type (a registry: a new owner is an entry here):
 * "Illustrative image for Dell Latitude 7420 (U-1)", "Storefront banner".
 */
const HEADINGS = {
  inventory_unit: (info) => `${(UNIT_FILE_KINDS[info.kind] || { noun: 'Image' }).noun} for ${info.owner_label}`,
  storefront: (info) => STOREFRONT_NOUNS[info.kind] || info.owner_label,
};
export const headingFor = (info) => (HEADINGS[info.owner_type] || ((i) => i.owner_label))(info);

const TYPE_NAMES = { 'image/jpeg': 'JPEG', 'image/png': 'PNG', 'image/webp': 'WebP', 'model/gltf-binary': 'GLB' };
export const typeNames = (mimes = []) => mimes.map((m) => TYPE_NAMES[m] || m).join(', ');

// File extensions for the picker: a browser does not always know a GLB's type.
const EXTENSIONS = { 'model/gltf-binary': '.glb' };
export const acceptFor = (mimes = []) => mimes.flatMap((m) => (EXTENSIONS[m] ? [m, EXTENSIONS[m]] : [m])).join(',');

export const sizeText = (bytes) => `${Math.round((Number(bytes) || 0) / (1024 * 1024))} MB`;

/** The Content-Type to send: the file's own when the link takes it, else the link's only type (a .glb the browser left untyped). */
export function contentTypeFor(file, mimes = []) {
  if (file && mimes.includes(file.type)) return file.type;
  if (mimes.length === 1) return mimes[0];
  return (file && file.type) || 'application/octet-stream';
}

/** The success line after the upload: an assistant's unit images wait for staff review. */
export const doneText = (reviewStatus) => (reviewStatus === 'pending' ? 'Uploaded — staff will review it' : 'Added');

export const EXPIRED_TEXT = 'This link has expired or was already used. Ask the assistant for a new one.';

/** What the person confirms before sending a unit photo (the link says attest_required; the PUT needs it). */
export const GENUINE_PHOTO_TEXT =
  'Only a genuine, unedited photo of this exact unit, taken by a person. No generated, AI-made, edited or stock images: those go as an illustrative image instead.';
export const GENUINE_PHOTO_CONFIRM = 'I confirm this is a genuine, unedited photo of this exact unit, taken by a person.';

/** Take the token out of the address bar (history, screenshots, a shared tab) once it is read: it lives in state only. */
export function stripHash(win = window) {
  if (!win.location.hash) return;
  win.history.replaceState(null, '', win.location.pathname + win.location.search);
}
